import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Shield, Users, Plus, Edit, Trash2, Crown, Building2,
    School, Trophy, Search, ChevronDown, ChevronUp, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
    master_admin: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    ministry_admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    education_admin: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    nock_admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    federation_admin: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    club_admin: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    school_admin: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    admin: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};

const ROLE_ICONS = {
    master_admin: <Crown className="w-4 h-4" />,
    ministry_admin: <Building2 className="w-4 h-4" />,
    education_admin: <School className="w-4 h-4" />,
    nock_admin: <Trophy className="w-4 h-4" />,
    federation_admin: <Shield className="w-4 h-4" />,
    club_admin: <Users className="w-4 h-4" />,
    school_admin: <School className="w-4 h-4" />,
};

const HIERARCHY_ROLES = [
    { value: 'master_admin', label: 'Master Admin (Level 6)', level: 6 },
    { value: 'ministry_admin', label: 'Ministry of Sports (Level 5)', level: 5 },
    { value: 'education_admin', label: 'Ministry of Education (Level 5)', level: 5 },
    { value: 'nock_admin', label: 'NOCK - Olympic Committee (Level 4)', level: 4 },
    { value: 'federation_admin', label: 'Sports Federation (Level 3)', level: 3 },
    { value: 'club_admin', label: 'Club Admin (Level 2)', level: 2 },
    { value: 'school_admin', label: 'School Admin (Level 1)', level: 1 },
];

export const HierarchyTab = ({ token }) => {
    const [admins, setAdmins] = useState([]);
    const [permissions, setPermissions] = useState(null);
    const [stats, setStats] = useState(null);
    const [sportsRegistry, setSportsRegistry] = useState([]);
    const [federations, setFederations] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showFedDialog, setShowFedDialog] = useState(false);
    const [showClubDialog, setShowClubDialog] = useState(false);
    const [showSchoolDialog, setShowSchoolDialog] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const [newAdmin, setNewAdmin] = useState({
        email: '', password: '', full_name: '', role: 'federation_admin',
        sport_id: '', federation_id: '', club_id: '', school_id: '', country: 'Kenya'
    });

    const [newFed, setNewFed] = useState({
        name: '', sport_id: '', country: 'Kenya', contact_person: '', contact_email: '', contact_phone: ''
    });

    const [newClub, setNewClub] = useState({
        name: '', sport_ids: [], country: 'Kenya', county: '', city: '', contact_person: '', contact_email: ''
    });

    const [newSchool, setNewSchool] = useState({
        name: '', sport_ids: [], institution_type: 'government_public_school',
        country: 'Kenya', county: '', city: '', contact_person: '', contact_email: ''
    });

    const headers = { Authorization: `Bearer ${token}` };

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [permRes, statsRes, sportsRes, fedsRes, clubsRes, schoolsRes, adminsRes] = await Promise.all([
                axios.get(`${API_URL}/api/rbac/my-permissions`, { headers }),
                axios.get(`${API_URL}/api/rbac/dashboard-stats`, { headers }),
                axios.get(`${API_URL}/api/rbac/sports-registry`, { headers }),
                axios.get(`${API_URL}/api/rbac/federations`, { headers }),
                axios.get(`${API_URL}/api/rbac/clubs`, { headers }),
                axios.get(`${API_URL}/api/rbac/schools`, { headers }),
                axios.get(`${API_URL}/api/rbac/admin-accounts`, { headers }).catch(() => ({ data: [] })),
            ]);
            setPermissions(permRes.data);
            setStats(statsRes.data);
            setSportsRegistry(sportsRes.data);
            setFederations(fedsRes.data);
            setClubs(clubsRes.data);
            setSchools(schoolsRes.data);
            setAdmins(adminsRes.data);
        } catch (err) {
            console.error('Failed to load RBAC data:', err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { loadData(); }, [loadData]);

    // Set default section based on role when permissions load
    useEffect(() => {
        if (permissions && activeSection === null) {
            const r = permissions.role || '';
            const isMasterRole = ['master_admin', 'admin', 'tournament_chairman'].includes(r);
            setActiveSection(isMasterRole ? 'overview' : 'admins');
        }
    }, [permissions, activeSection]);

    const createAdmin = async () => {
        try {
            await axios.post(`${API_URL}/api/rbac/admin-accounts`, newAdmin, { headers });
            toast.success('Admin account created');
            setShowCreateDialog(false);
            setNewAdmin({ email: '', password: '', full_name: '', role: 'federation_admin', sport_id: '', federation_id: '', club_id: '', school_id: '', country: 'Kenya' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create admin');
        }
    };

    const deleteAdmin = async (userId) => {
        if (!window.confirm('Delete this admin account?')) return;
        try {
            await axios.delete(`${API_URL}/api/rbac/admin-accounts/${userId}`, { headers });
            toast.success('Admin deleted');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
        }
    };

    const createFederation = async () => {
        try {
            await axios.post(`${API_URL}/api/rbac/federations`, newFed, { headers });
            toast.success('Federation created');
            setShowFedDialog(false);
            setNewFed({ name: '', sport_id: '', country: 'Kenya', contact_person: '', contact_email: '', contact_phone: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create federation');
        }
    };

    const deleteFederation = async (fedId) => {
        if (!window.confirm('Delete this federation?')) return;
        try {
            await axios.delete(`${API_URL}/api/rbac/federations/${fedId}`, { headers });
            toast.success('Federation deleted');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete');
        }
    };

    const createClub = async () => {
        try {
            await axios.post(`${API_URL}/api/rbac/clubs`, newClub, { headers });
            toast.success('Club created');
            setShowClubDialog(false);
            setNewClub({ name: '', sport_ids: [], country: 'Kenya', county: '', city: '', contact_person: '', contact_email: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create club');
        }
    };

    const createSchoolOrg = async () => {
        try {
            await axios.post(`${API_URL}/api/rbac/schools`, newSchool, { headers });
            toast.success('School organization created');
            setShowSchoolDialog(false);
            setNewSchool({ name: '', sport_ids: [], institution_type: 'government_public_school', country: 'Kenya', county: '', city: '', contact_person: '', contact_email: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create school');
        }
    };

    const generateAthleteIds = async () => {
        try {
            const res = await axios.post(`${API_URL}/api/rbac/generate-athlete-ids`, {}, { headers });
            toast.success(`Generated ${res.data.generated_count} Athlete IDs`);
            loadData();
        } catch (err) {
            toast.error('Failed to generate IDs');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const role = permissions?.role || '';
    const isMaster = ['master_admin', 'admin', 'tournament_chairman'].includes(role);
    const isMinistrySports = role === 'ministry_admin';
    const isEducation = role === 'education_admin';
    const isNock = role === 'nock_admin';
    const isFederation = role === 'federation_admin';
    const isClub = role === 'club_admin';
    const isSchool = role === 'school_admin';

    // Build sections based on role
    const allSections = [
        { id: 'overview', label: 'Overview', icon: <Eye className="w-4 h-4" />, roles: ['master_admin', 'admin', 'tournament_chairman'] },
        { id: 'admins', label: 'Accounts', icon: <Shield className="w-4 h-4" />, roles: 'all' },
        { id: 'sports', label: 'Sports Registry', icon: <Trophy className="w-4 h-4" />, roles: ['master_admin', 'admin', 'tournament_chairman', 'ministry_admin'] },
        { id: 'federations', label: 'Federations', icon: <Building2 className="w-4 h-4" />, roles: ['master_admin', 'admin', 'tournament_chairman', 'ministry_admin', 'nock_admin'] },
        { id: 'clubs', label: 'Clubs', icon: <Users className="w-4 h-4" />, roles: ['master_admin', 'admin', 'tournament_chairman', 'federation_admin'] },
        { id: 'schools', label: 'Schools', icon: <School className="w-4 h-4" />, roles: ['master_admin', 'admin', 'tournament_chairman', 'education_admin'] },
    ];

    const sections = allSections.filter(s => 
        s.roles === 'all' || s.roles.includes(role)
    );

    // Default to 'admins' for non-master roles
    const defaultSection = isMaster ? 'overview' : 'admins';

    return (
        <div className="space-y-6" data-testid="hierarchy-tab">
            {/* Section tabs */}
            <div className="flex flex-wrap gap-2">
                {sections.map(s => (
                    <Button
                        key={s.id}
                        variant={activeSection === s.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setActiveSection(s.id)}
                        className={activeSection === s.id ? 'bg-cyan-600' : 'border-white/20'}
                        data-testid={`section-${s.id}`}
                    >
                        {s.icon}
                        <span className="ml-1.5">{s.label}</span>
                    </Button>
                ))}
            </div>

            {/* OVERVIEW */}
            {activeSection === 'overview' && (
                <div className="space-y-6">
                    {/* Stats cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-[#0d2847] border border-white/10 p-4 rounded">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Sports</p>
                            <p className="text-2xl font-bold text-cyan-400 mt-1">{stats?.total_sports || 0}</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/10 p-4 rounded">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Federations</p>
                            <p className="text-2xl font-bold text-emerald-400 mt-1">{stats?.total_federations || 0}</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/10 p-4 rounded">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Clubs</p>
                            <p className="text-2xl font-bold text-orange-400 mt-1">{stats?.total_clubs || 0}</p>
                        </div>
                        <div className="bg-[#0d2847] border border-white/10 p-4 rounded">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">Athletes with ID</p>
                            <p className="text-2xl font-bold text-yellow-400 mt-1">{stats?.athletes_with_id || 0}</p>
                        </div>
                    </div>

                    {/* Hierarchy pyramid */}
                    <div className="bg-[#0d2847] border border-white/10 p-6 rounded">
                        <h3 className="font-bold text-lg mb-4 uppercase tracking-tight">Admin Hierarchy</h3>
                        <div className="space-y-2">
                            {HIERARCHY_ROLES.map(r => {
                                const count = admins.filter(a => a.role === r.value).length;
                                return (
                                    <div key={r.value} className="flex items-center gap-3">
                                        <div className={`px-3 py-1.5 rounded text-xs font-medium border ${ROLE_COLORS[r.value] || 'bg-white/10 text-white'}`}>
                                            <span className="flex items-center gap-1.5">
                                                {ROLE_ICONS[r.value]}
                                                Level {r.level}
                                            </span>
                                        </div>
                                        <span className="text-sm flex-1">{r.label}</span>
                                        <span className="text-xs text-muted-foreground">{count} account{count !== 1 ? 's' : ''}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* National Athlete ID */}
                    <div className="bg-[#0d2847] border border-white/10 p-6 rounded">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-bold text-lg uppercase tracking-tight">National Athlete ID System</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {stats?.athletes_with_id || 0} of {stats?.total_athletes || 0} athletes have IDs
                                </p>
                            </div>
                            <Button onClick={generateAthleteIds} className="bg-cyan-600 hover:bg-cyan-700" data-testid="generate-ids-btn">
                                Generate Missing IDs
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Format: CountryCode-SportCode-Year-Number (e.g., KEN-TEN-2026-004532)
                        </p>
                    </div>
                </div>
            )}

            {/* ADMIN ACCOUNTS */}
            {activeSection === 'admins' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-lg uppercase tracking-tight">
                                {isMaster && `All Admin Accounts (${admins.length})`}
                                {isMinistrySports && `Federation & Organization Accounts (${admins.length})`}
                                {isEducation && `School Accounts (${admins.length})`}
                                {isNock && `Olympic Federation Accounts (${admins.length})`}
                                {isFederation && `Club Accounts (${admins.length})`}
                                {(isClub || isSchool) && `My Account`}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                {isMinistrySports && 'All federation, NOCK, club, and school admin accounts'}
                                {isEducation && 'School admin accounts under your oversight'}
                                {isNock && 'Federation accounts for Olympic sports only'}
                                {isFederation && `Club accounts for ${permissions?.accessible_sports?.[0] || 'your sport'}`}
                                {(isClub || isSchool) && 'Your admin account details'}
                            </p>
                        </div>
                        {isMaster && (
                            <Button onClick={() => setShowCreateDialog(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-admin-btn">
                                <Plus className="w-4 h-4 mr-1" /> Create Admin
                            </Button>
                        )}
                    </div>
                    <div className="space-y-2">
                        {admins.map(admin => (
                            <div key={admin.id} className="bg-[#0d2847] border border-white/10 p-4 rounded flex items-center justify-between" data-testid={`admin-row-${admin.id}`}>
                                <div className="flex items-center gap-3">
                                    <div className={`px-2 py-1 rounded text-xs font-medium border ${ROLE_COLORS[admin.role] || 'bg-white/10'}`}>
                                        <span className="flex items-center gap-1">
                                            {ROLE_ICONS[admin.role] || <Shield className="w-3 h-3" />}
                                            L{admin.level}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">{admin.full_name}</p>
                                        <p className="text-xs text-muted-foreground">{admin.email} — {admin.display_role}</p>
                                    </div>
                                </div>
                                {isMaster && (
                                    <Button variant="ghost" size="sm" onClick={() => deleteAdmin(admin.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-admin-${admin.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        ))}
                        {admins.length === 0 && (
                            <div className="text-center py-12 text-muted-foreground">
                                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No accounts found for your access level</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* SPORTS REGISTRY */}
            {activeSection === 'sports' && (
                <div className="space-y-4">
                    <h3 className="font-bold text-lg uppercase tracking-tight">Master Sports Registry ({sportsRegistry.length} sports)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {sportsRegistry.map(sport => (
                            <div key={sport.sport_id} className="bg-[#0d2847] border border-white/10 p-4 rounded" data-testid={`sport-${sport.sport_id}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{sport.sport_name}</p>
                                        <div className="flex gap-2 mt-1">
                                            <span className={`text-xs px-2 py-0.5 rounded ${sport.olympic_status ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-muted-foreground'}`}>
                                                {sport.olympic_status ? 'Olympic' : 'Non-Olympic'}
                                            </span>
                                            {sport.federation_assigned && (
                                                <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Federation Assigned</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {sport.disciplines?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {sport.disciplines.slice(0, 5).map(d => (
                                            <span key={d} className="text-xs px-1.5 py-0.5 bg-white/5 rounded">{d}</span>
                                        ))}
                                        {sport.disciplines.length > 5 && (
                                            <span className="text-xs px-1.5 py-0.5 text-muted-foreground">+{sport.disciplines.length - 5} more</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* FEDERATIONS */}
            {activeSection === 'federations' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold text-lg uppercase tracking-tight">
                            {isNock ? `Olympic Federations (${federations.length})` : `Sports Federations (${federations.length})`}
                        </h3>
                        {isMaster && (
                            <Button onClick={() => setShowFedDialog(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-federation-btn">
                                <Plus className="w-4 h-4 mr-1" /> Create Federation
                            </Button>
                        )}
                    </div>
                    {federations.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No federations found for your access level.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {federations.map(fed => (
                                <div key={fed.federation_id} className="bg-[#0d2847] border border-white/10 p-4 rounded flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{fed.name}</p>
                                        <p className="text-xs text-muted-foreground">Sport: {fed.sport_name} | {fed.country}</p>
                                        {fed.contact_person && <p className="text-xs text-muted-foreground mt-1">Contact: {fed.contact_person}</p>}
                                    </div>
                                    {isMaster && (
                                        <Button variant="ghost" size="sm" onClick={() => deleteFederation(fed.federation_id)} className="text-red-400 hover:text-red-300">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* CLUBS */}
            {activeSection === 'clubs' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold text-lg uppercase tracking-tight">
                            {isFederation ? `${permissions?.accessible_sports?.[0] || 'Sport'} Clubs (${clubs.length})` : `Clubs (${clubs.length})`}
                        </h3>
                        {(isMaster || isFederation) && (
                            <Button onClick={() => setShowClubDialog(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-club-btn">
                                <Plus className="w-4 h-4 mr-1" /> Create Club
                            </Button>
                        )}
                    </div>
                    {clubs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No clubs registered yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {clubs.map(club => (
                                <div key={club.club_id} className="bg-[#0d2847] border border-white/10 p-4 rounded flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{club.name}</p>
                                        <p className="text-xs text-muted-foreground">{club.country} {club.county ? `- ${club.county}` : ''}</p>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {club.sport_names?.map(s => (
                                                <span key={s} className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                    {(isMaster || isFederation) && (
                                        <Button variant="ghost" size="sm" onClick={async () => {
                                            if (!window.confirm('Delete this club?')) return;
                                            try {
                                                await axios.delete(`${API_URL}/api/rbac/clubs/${club.club_id}`, { headers });
                                                toast.success('Club deleted');
                                                loadData();
                                            } catch (err) { toast.error('Failed to delete club'); }
                                        }} className="text-red-400 hover:text-red-300">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* SCHOOLS (orgs) */}
            {activeSection === 'schools' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold text-lg uppercase tracking-tight">School Organizations ({schools.length})</h3>
                        {(isMaster || isEducation) && (
                            <Button onClick={() => setShowSchoolDialog(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-school-org-btn">
                                <Plus className="w-4 h-4 mr-1" /> Create School
                            </Button>
                        )}
                    </div>
                    {schools.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <School className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No school organizations registered yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {schools.map(sch => (
                                <div key={sch.school_id} className="bg-[#0d2847] border border-white/10 p-4 rounded flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{sch.name}</p>
                                        <p className="text-xs text-muted-foreground">{sch.institution_type?.replace(/_/g, ' ')} | {sch.country}</p>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {sch.sport_names?.map(s => (
                                                <span key={s} className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                    {(isMaster || isEducation) && (
                                        <Button variant="ghost" size="sm" onClick={async () => {
                                            if (!window.confirm('Delete this school?')) return;
                                            try {
                                                await axios.delete(`${API_URL}/api/rbac/schools/${sch.school_id}`, { headers });
                                                toast.success('School deleted');
                                                loadData();
                                            } catch (err) { toast.error('Failed to delete school'); }
                                        }} className="text-red-400 hover:text-red-300">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* CREATE ADMIN DIALOG */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create Admin Account</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Full Name</Label>
                            <Input value={newAdmin.full_name} onChange={e => setNewAdmin({...newAdmin, full_name: e.target.value})} className="bg-[#0a1628] border-white/10" data-testid="new-admin-name" />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input type="email" value={newAdmin.email} onChange={e => setNewAdmin({...newAdmin, email: e.target.value})} className="bg-[#0a1628] border-white/10" data-testid="new-admin-email" />
                        </div>
                        <div>
                            <Label>Password</Label>
                            <Input type="password" value={newAdmin.password} onChange={e => setNewAdmin({...newAdmin, password: e.target.value})} className="bg-[#0a1628] border-white/10" data-testid="new-admin-password" />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={newAdmin.role} onValueChange={v => setNewAdmin({...newAdmin, role: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10" data-testid="new-admin-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {HIERARCHY_ROLES.map(r => (
                                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {newAdmin.role === 'federation_admin' && (
                            <div>
                                <Label>Sport</Label>
                                <Select value={newAdmin.sport_id} onValueChange={v => setNewAdmin({...newAdmin, sport_id: v})}>
                                    <SelectTrigger className="bg-[#0a1628] border-white/10">
                                        <SelectValue placeholder="Select sport" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#0d2847] border-white/10">
                                        {sportsRegistry.map(s => (
                                            <SelectItem key={s.sport_id} value={s.sport_id}>{s.sport_name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/20">Cancel</Button>
                        <Button onClick={createAdmin} className="bg-cyan-600" data-testid="submit-create-admin">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CREATE FEDERATION DIALOG */}
            <Dialog open={showFedDialog} onOpenChange={setShowFedDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create Federation</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Federation Name</Label>
                            <Input value={newFed.name} onChange={e => setNewFed({...newFed, name: e.target.value})} className="bg-[#0a1628] border-white/10" placeholder="e.g., Kenya Tennis Federation" data-testid="new-fed-name" />
                        </div>
                        <div>
                            <Label>Sport</Label>
                            <Select value={newFed.sport_id} onValueChange={v => setNewFed({...newFed, sport_id: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10" data-testid="new-fed-sport">
                                    <SelectValue placeholder="Select sport" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {sportsRegistry.map(s => (
                                        <SelectItem key={s.sport_id} value={s.sport_id}>{s.sport_name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Contact Person</Label>
                            <Input value={newFed.contact_person} onChange={e => setNewFed({...newFed, contact_person: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                        <div>
                            <Label>Contact Email</Label>
                            <Input type="email" value={newFed.contact_email} onChange={e => setNewFed({...newFed, contact_email: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowFedDialog(false)} className="border-white/20">Cancel</Button>
                        <Button onClick={createFederation} className="bg-cyan-600" data-testid="submit-create-fed">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CREATE CLUB DIALOG */}
            <Dialog open={showClubDialog} onOpenChange={setShowClubDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create Club</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Club Name</Label>
                            <Input value={newClub.name} onChange={e => setNewClub({...newClub, name: e.target.value})} className="bg-[#0a1628] border-white/10" data-testid="new-club-name" />
                        </div>
                        <div>
                            <Label>Sports (select from registry)</Label>
                            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto mt-1">
                                {sportsRegistry.map(s => (
                                    <label key={s.sport_id} className="flex items-center gap-1.5 text-xs cursor-pointer p-1 hover:bg-white/5 rounded">
                                        <input
                                            type="checkbox"
                                            checked={newClub.sport_ids.includes(s.sport_id)}
                                            onChange={e => {
                                                const ids = e.target.checked
                                                    ? [...newClub.sport_ids, s.sport_id]
                                                    : newClub.sport_ids.filter(id => id !== s.sport_id);
                                                setNewClub({...newClub, sport_ids: ids});
                                            }}
                                            className="accent-cyan-500"
                                        />
                                        {s.sport_name}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label>County</Label>
                            <Input value={newClub.county} onChange={e => setNewClub({...newClub, county: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                        <div>
                            <Label>City</Label>
                            <Input value={newClub.city} onChange={e => setNewClub({...newClub, city: e.target.value})} className="bg-[#0a1628] border-white/10" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowClubDialog(false)} className="border-white/20">Cancel</Button>
                        <Button onClick={createClub} className="bg-cyan-600" data-testid="submit-create-club">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CREATE SCHOOL ORG DIALOG */}
            <Dialog open={showSchoolDialog} onOpenChange={setShowSchoolDialog}>
                <DialogContent className="bg-[#0d2847] border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create School Organization</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>School Name</Label>
                            <Input value={newSchool.name} onChange={e => setNewSchool({...newSchool, name: e.target.value})} className="bg-[#0a1628] border-white/10" data-testid="new-school-name" />
                        </div>
                        <div>
                            <Label>Type</Label>
                            <Select value={newSchool.institution_type} onValueChange={v => setNewSchool({...newSchool, institution_type: v})}>
                                <SelectTrigger className="bg-[#0a1628] border-white/10">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    <SelectItem value="international_school">International School</SelectItem>
                                    <SelectItem value="private_school">Private School</SelectItem>
                                    <SelectItem value="government_public_school">Public School</SelectItem>
                                    <SelectItem value="sports_academy">Sports Academy</SelectItem>
                                    <SelectItem value="community_sports_club">Community Sports Club</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Sports</Label>
                            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto mt-1">
                                {sportsRegistry.map(s => (
                                    <label key={s.sport_id} className="flex items-center gap-1.5 text-xs cursor-pointer p-1 hover:bg-white/5 rounded">
                                        <input
                                            type="checkbox"
                                            checked={newSchool.sport_ids.includes(s.sport_id)}
                                            onChange={e => {
                                                const ids = e.target.checked
                                                    ? [...newSchool.sport_ids, s.sport_id]
                                                    : newSchool.sport_ids.filter(id => id !== s.sport_id);
                                                setNewSchool({...newSchool, sport_ids: ids});
                                            }}
                                            className="accent-cyan-500"
                                        />
                                        {s.sport_name}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSchoolDialog(false)} className="border-white/20">Cancel</Button>
                        <Button onClick={createSchoolOrg} className="bg-cyan-600" data-testid="submit-create-school">Create</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default HierarchyTab;
HierarchyTab;
